"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef } from "react";

interface UseNextDrawerBackInterceptorOptions {
    open: boolean;
    onClose: () => void;
    enabled?: boolean;
    /** 区分不同 Drawer / Modal */
    key?: string;
}

export function useNextDrawerBackInterceptor( {
    open,
    onClose,
    enabled = true,
    key = "__drawer__",
}: UseNextDrawerBackInterceptorOptions ) {
    const localKey = useRef( key );
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const fullUrl = useMemo( () => {
        const query = searchParams?.toString();
        return query ? `${pathname}?${query}` : pathname;
    }, [ pathname, searchParams ] );

    const pushedRef = useRef( false );
    // 新增：标记是否是我们主动调用的 history.back()，用来拦截随后的 popstate
    const internalBackRef = useRef( false );

    // 用 ref 追踪最新的 open / onClose，避免闭包捕获过期值
    const openRef = useRef( open );
    openRef.current = open;
    const onCloseRef = useRef( onClose );
    onCloseRef.current = onClose;

    // 1. 处理打开逻辑：压入状态
    useEffect( () => {
        if ( !enabled || !open ) return;

        // 检查是否已经压入过
        if ( !pushedRef.current ) {
            // 检查当前 history 的 top state 是否已经有了我们的 key（例如复制新标签页、或者页面强刷的场景）
            if ( history.state?.[localKey.current] !== true ) {
                history.pushState( { ...history.state, [localKey.current]: true }, "", fullUrl );
            }
            pushedRef.current = true;
        }
    }, [ open, enabled, fullUrl ] );

    // 2. 处理后退拦截（只注册一次，通过 ref 读取最新状态）
    //    关键：capture: true 让我们在 Next.js 之前拦截
    //    stopImmediatePropagation 阻止 Next.js 将其视为路由导航
    useEffect( () => {
        if ( !enabled ) return;

        const onPopState = ( event: PopStateEvent ) => {
            // 拦截到我们自己主动触发的后退动作
            if ( internalBackRef.current ) {
                internalBackRef.current = false;
                event.stopImmediatePropagation();
                return;
            }

            if ( !openRef.current || !pushedRef.current ) return;

            // 新的 state 里没了我们的 key → 用户按了原生的物理后退
            if ( !event.state?.[localKey.current] ) {
                pushedRef.current = false;
                event.stopImmediatePropagation(); // 阻止 Next.js 处理
                onCloseRef.current();
            }
        };

        // capture: true → 在 Next.js 的 bubble 监听器之前执行
        window.addEventListener( "popstate", onPopState, true );
        return () => window.removeEventListener( "popstate", onPopState, true );
    }, [ enabled ] );

    // 3. 弹窗主动关闭时（非回退触发），清理 history
    useEffect( () => {
        if ( !enabled ) return;

        if ( !open && pushedRef.current ) {
            pushedRef.current = false;
            if ( history.state?.[localKey.current] ) {
                internalBackRef.current = true;
                history.back();
                // 如果极少数情况下 history.back() 没能成功触发 popstate，我们需要恢复标志位
                // 一般情况下会立刻在 0-few ms 内触发 popstate
                setTimeout( () => {
                    internalBackRef.current = false;
                }, 100 );
            }
        }
    }, [ open, enabled ] );

    // 4. 卸载清理
    useEffect( () => {
        return () => {
            if ( pushedRef.current ) {
                pushedRef.current = false;
                if ( history.state?.[localKey.current] ) {
                    // 注意这里如果组件卸载了，监听器已经被移除了，Next.js 会处理后退动作
                    // 我们可以暂时不做特殊拦截，仅仅恢复 history stack
                    history.back();
                }
            }
        };
    }, [] );
}
